import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/redux-toolkit/store';
import { useToken } from '@/common/helper';
import {
  GetLocationsWithPaginationAndSearch,
  GetLocationsWithPaginationAndSearchResponse,
} from '@/apis/onboarding';

import useUnauthorize from '@/hooks/useUnauthorize';
import {
  GetLocationsType,
  GetSettingsCompanySensors,
  GetUsersByTeamIds,
} from '@/apis/settings';
import { buildAlertAssignmentOptions } from '@/pages/Shipments/Filters/utils/alertAssignmentOptions';
import { ErrorLike, LocationData } from '@/apis/types';

import {
  getServiceLevelCouriersDDL,
  getTeamsDDLOFFeature,
} from '@/common/utils/dropDown';

import {
  GetProducts,
  GetGroupedLocationsWithPagination,
  GetLocationsParsedListWithPagination,
} from '@/apis/onboarding';
import {
  GetProductTypesWithPaginationAndSearch,
  GetTeamsDDL,
} from '@/apis/lookup';
import { GetShipmentsStatus, GetShipmentRtgCodes } from '@/apis/shipment';

import { fetchAllCouriers } from '@/redux-toolkit/slice/onboardingSlice';
import { isZoomlogiSupport } from '@/common/utils/utils';

import {
  healthStatusDDL,
  deliveryStatusDDL,
  PODStatusOptions,
} from '@/common/static_data';
import { selectProductState } from '@/redux-toolkit/slice/ProductSlice';
import {
  FilterLookups,
  GroupedLocationItem,
  ListItem,
} from '@/pages/Shipments/ControlTower/types';

const LOOKUP_CACHE_TTL_MS = 10 * 60 * 1000;

type LoaderKey =
  | 'couriers'
  | 'teams'
  | 'products'
  | 'sensors'
  | 'alert_assignment'
  | 'shipment_status'
  | 'shipment_status_el'
  | 'location_types'
  | 'rtg_codes';

type LoaderState = {
  loaded: boolean;
  inFlight: Promise<ListItem[]> | null;
};

export default function useFilterLookups(): FilterLookups & {
  alertAssignmentOptions?: ListItem[];
  loaders: {
    alertAssignment?: () => Promise<ListItem[]>;
    couriers: () => Promise<ListItem[]>;
    teams: () => Promise<ListItem[]>;
    products: () => Promise<ListItem[]>;
    sensors: () => Promise<ListItem[]>;
    shipmentStatus: (legType?: 'CL' | 'EL') => Promise<ListItem[]>;
    locationTypes: () => Promise<ListItem[]>;
    shipperRtg: () => Promise<ListItem[]>;
    consigneeRtg: () => Promise<ListItem[]>;
  };
  resetLookups: () => void;
} {
  const { token, decodedUserToken } = useToken();
  const { unauthorize } = useUnauthorize();
  const dispatch = useDispatch<AppDispatch>();

  const state = useSelector((s: RootState) => s);

  const [couriersList, setCouriersList] = useState<ListItem[]>([]);
  const [productTypeList, setProductTypeList] = useState<ListItem[]>([]);
  const [productList, setProductList] = useState<ListItem[]>([]);
  const [sensorsList, setSensorsList] = useState<ListItem[]>([]);
  const [alertAssignmentOptions, setAlertAssignmentOptions] = useState<
    ListItem[]
  >([]);
  const [projectIdList, setProjectIdList] = useState<ListItem[]>([]);
  const [locationTypeList, setLocationTypeList] = useState<ListItem[]>([]);
  const [shipperRtgList, setShipperRtgList] = useState<ListItem[]>([]);
  const [consigneeRtgList, setConsigneeRtgList] = useState<ListItem[]>([]);
  const [allTeams, setAllTeams] = useState<ListItem[]>([]);
  const [shipmentStatusDDL, setShipmentStatusDDL] = useState<ListItem[]>([]);
  const [shipmentStatusELDDL, setShipmentStatusELDDL] = useState<ListItem[]>(
    []
  );
  // const [shipmentLinkCodes, setShipmentLinkCodes] = useState<ListItem[]>([]);
  const [statesAndCities] = useState<ListItem[]>([]);

  const storeCouriers = state?.Couriers?.courierList;
  const projectsID = useSelector(selectProductState);

  const companyName = decodedUserToken()?.company_name;

  const loadersRef = useRef<Record<LoaderKey, LoaderState>>({
    couriers: { loaded: false, inFlight: null },
    teams: { loaded: false, inFlight: null },
    products: { loaded: false, inFlight: null },
    sensors: { loaded: false, inFlight: null },
    alert_assignment: { loaded: false, inFlight: null },
    shipment_status: { loaded: false, inFlight: null },
    shipment_status_el: { loaded: false, inFlight: null },
    location_types: { loaded: false, inFlight: null },
    rtg_codes: { loaded: false, inFlight: null },
  });

  const cacheKeyFor = useCallback(
    (name: string) => {
      const decoded = decodedUserToken();
      const company = decoded?.company_id || 'unknown_company';
      const user = decoded?.user_id || 'unknown_user';
      return `ctl:${name}:c:${company}:u:${user}:v2`;
    },
    [decodedUserToken]
  );

  const resetLookups = useCallback(() => {
    setCouriersList([]);
    setProductTypeList([]);
    setProductList([]);
    setSensorsList([]);
    setAlertAssignmentOptions([]);
    setProjectIdList([]);
    setLocationTypeList([]);
    setShipperRtgList([]);
    setConsigneeRtgList([]);
    setAllTeams([]);
    setShipmentStatusDDL([]);
    setShipmentStatusELDDL([]);

    loadersRef.current = {
      couriers: { loaded: false, inFlight: null },
      teams: { loaded: false, inFlight: null },
      products: { loaded: false, inFlight: null },
      sensors: { loaded: false, inFlight: null },
      alert_assignment: { loaded: false, inFlight: null },
      shipment_status: { loaded: false, inFlight: null },
      shipment_status_el: { loaded: false, inFlight: null },
      location_types: { loaded: false, inFlight: null },
      rtg_codes: { loaded: false, inFlight: null },
    };

    try {
      localStorage.removeItem(cacheKeyFor('couriers'));
      localStorage.removeItem(cacheKeyFor('teams'));
      localStorage.removeItem(cacheKeyFor('products'));
      localStorage.removeItem(cacheKeyFor('sensors'));
      localStorage.removeItem(cacheKeyFor('company_sensors'));
      localStorage.removeItem(cacheKeyFor('shipment_status'));
      localStorage.removeItem(cacheKeyFor('shipment_status_el'));
      localStorage.removeItem(cacheKeyFor('location_types'));
      localStorage.removeItem(cacheKeyFor('rtg_codes'));
    } catch {
      //
    }
  }, [cacheKeyFor]);

  const isCacheValid = useCallback((cached: unknown): cached is ListItem[] => {
    return Array.isArray(cached) && cached.length > 0;
  }, []);

  const readLookupCache = useCallback(
    (name: string) => {
      try {
        const key = cacheKeyFor(name);
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.expires || parsed.expires < Date.now()) {
          localStorage.removeItem(key);
          return null;
        }
        return parsed.data ?? null;
      } catch {
        return null;
      }
    },
    [cacheKeyFor]
  );

  const writeLookupCache = useCallback(
    (name: string, data: unknown) => {
      try {
        const key = cacheKeyFor(name);
        const payload = {
          data,
          expires: Date.now() + LOOKUP_CACHE_TTL_MS,
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch {
        // ignore
      }
    },
    [cacheKeyFor]
  );

  const runOnce = useCallback(
    (key: LoaderKey, fn: () => Promise<ListItem[]>) => {
      const slot = loadersRef.current[key];
      if (slot.loaded) return Promise.resolve([]);

      if (slot.inFlight) return slot.inFlight;

      slot.inFlight = fn()
        .then((list) => {
          slot.loaded = true;
          return list;
        })
        .finally(() => {
          slot.inFlight = null;
        });

      return slot.inFlight;
    },
    []
  );

  const loadTeams = useCallback(() => {
    if (!token) {
      return Promise.resolve([]);
    }
    return runOnce('teams', async () => {
      const cached = readLookupCache('teams');
      if (isCacheValid(cached)) {
        const sorted = [...cached].sort((a, b) => {
          const aDefault = (a.name || '').toLowerCase() === 'default' ? 0 : 1;
          const bDefault = (b.name || '').toLowerCase() === 'default' ? 0 : 1;
          if (aDefault !== bDefault) return aDefault - bDefault;
          return (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
          });
        });
        setAllTeams(sorted);
        return sorted;
      }

      const val = await getTeamsDDLOFFeature(token, 'Shipment');
      const safe = Array.isArray(val) ? val : [];
      if (safe.length) {
        setAllTeams(safe);
        writeLookupCache('teams', safe);
      }
      return safe;
    });
  }, [runOnce, token, readLookupCache, writeLookupCache, isCacheValid]);

  const loadAlertAssignment = useCallback(() => {
    if (!token) return Promise.resolve([]);
    return runOnce('alert_assignment', async () => {
      try {
        // Teams the user can see for Alerts, then those teams' members.
        const teamsResponse = await GetTeamsDDL(token, 'Alerts');
        const teams = teamsResponse.data.data.is_success
          ? (teamsResponse.data.data.data ?? [])
          : [];
        const usersResponse = teams.length
          ? await GetUsersByTeamIds(
              token,
              teams.map((team) => team.id)
            )
          : null;
        const teamsUsers = usersResponse?.data.data.is_success
          ? (usersResponse.data.data.data.teams_users ?? [])
          : [];
        const options = buildAlertAssignmentOptions({ teams, teamsUsers });
        setAlertAssignmentOptions(options);
        return options;
      } catch (error) {
        unauthorize(error as ErrorLike);
        return [];
      }
    });
  }, [token, runOnce, unauthorize]);

  const loadLocationTypes = useCallback(() => {
    if (!token) return Promise.resolve([]);

    return runOnce('location_types', async () => {
      const cached = readLookupCache('location_types');
      if (isCacheValid(cached)) {
        setLocationTypeList(cached);
        return cached;
      }

      try {
        const res = await GetLocationsType(token);
        const raw = res?.data?.data?.data ?? [];

        const safe: ListItem[] = Array.isArray(raw)
          ? raw.map((item) => ({
              id: String(item.id),
              name: item.name ?? '',
              title: item.name ?? '',
            }))
          : [];

        if (safe.length) {
          setLocationTypeList(safe);
          writeLookupCache('location_types', safe);
        }

        return safe;
      } catch (e) {
        unauthorize(e as ErrorLike);
        return [];
      }
    });
  }, [
    token,
    runOnce,
    readLookupCache,
    writeLookupCache,
    isCacheValid,
    unauthorize,
  ]);

  const rtgInFlightRef = useRef<Promise<{
    shipper: ListItem[];
    consignee: ListItem[];
  }> | null>(null);

  const loadRtgCodes = useCallback(() => {
    if (!token) {
      return Promise.resolve({
        shipper: [] as ListItem[],
        consignee: [] as ListItem[],
      });
    }
    if (rtgInFlightRef.current) return rtgInFlightRef.current;

    rtgInFlightRef.current = (async () => {
      try {
        const res = await GetShipmentRtgCodes(token);
        const data = (res?.data?.data?.data ?? res?.data?.data ?? {}) as {
          shipper_rtg?: unknown;
          consignee_rtg?: unknown;
        };
        const shipperRaw: string[] = Array.isArray(data.shipper_rtg)
          ? (data.shipper_rtg as string[])
          : [];
        const consigneeRaw: string[] = Array.isArray(data.consignee_rtg)
          ? (data.consignee_rtg as string[])
          : [];
        const shipper: ListItem[] = shipperRaw.map((code) => ({
          id: code,
          name: code,
          title: code,
        }));
        const consignee: ListItem[] = consigneeRaw.map((code) => ({
          id: code,
          name: code,
          title: code,
        }));
        setShipperRtgList(shipper);
        setConsigneeRtgList(consignee);
        loadersRef.current.rtg_codes.loaded = true;
        return { shipper, consignee };
      } catch (e) {
        unauthorize(e as ErrorLike);
        return { shipper: [] as ListItem[], consignee: [] as ListItem[] };
      } finally {
        rtgInFlightRef.current = null;
      }
    })();

    return rtgInFlightRef.current;
  }, [token, unauthorize]);

  const loadShipperRtg = useCallback(() => {
    if (shipperRtgList.length || loadersRef.current.rtg_codes.loaded) {
      return Promise.resolve(shipperRtgList);
    }
    return loadRtgCodes().then((bundle) => bundle.shipper);
  }, [loadRtgCodes, shipperRtgList]);

  const loadConsigneeRtg = useCallback(() => {
    if (consigneeRtgList.length || loadersRef.current.rtg_codes.loaded) {
      return Promise.resolve(consigneeRtgList);
    }
    return loadRtgCodes().then((bundle) => bundle.consignee);
  }, [loadRtgCodes, consigneeRtgList]);
  const loadShipmentStatus = useCallback(
    (legType?: 'CL' | 'EL') => {
      if (!token) {
        return Promise.resolve([]);
      }
      const isEL = legType === 'EL';
      const cacheKey: LoaderKey = isEL
        ? 'shipment_status_el'
        : 'shipment_status';
      const setStatus = isEL ? setShipmentStatusELDDL : setShipmentStatusDDL;

      const slot = loadersRef.current[cacheKey];

      // If already loaded this session, re-hydrate from localStorage cache
      // (the shared shipmentStatusDDL state may have been overwritten by the
      // other leg type's fetch — see ZD-XXXX). We bypass runOnce here so we
      // can always restore the correct leg-type data into the correct state.
      if (slot.loaded) {
        const cached = readLookupCache(cacheKey);
        if (isCacheValid(cached)) {
          setStatus(cached);
          return Promise.resolve(cached);
        }
        // Cache expired — allow a fresh fetch by resetting the slot
        slot.loaded = false;
      }

      return runOnce(cacheKey, async () => {
        const cached = readLookupCache(cacheKey);
        if (isCacheValid(cached)) {
          setStatus(cached);
          return cached;
        }

        const res = await GetShipmentsStatus(token, legType);
        const data = res?.data || null;

        const safe: ListItem[] =
          data?.data?.is_success && Array.isArray(data?.data?.data)
            ? data.data.data
            : [];

        if (safe.length) {
          setStatus(safe);
          writeLookupCache(cacheKey, safe);
        }

        return safe;
      });
    },
    [runOnce, token, readLookupCache, writeLookupCache, isCacheValid]
  );

  const loadProducts = useCallback(() => {
    if (!token) {
      return Promise.resolve([]);
    }
    return runOnce('products', async () => {
      const cached = readLookupCache('products');
      if (isCacheValid(cached)) {
        setProductList(cached);
        return cached;
      }

      try {
        const res = await GetProducts(token);
        const data = res?.data || null;
        const safe: ListItem[] =
          data?.data?.is_success && Array.isArray(data?.data?.data)
            ? data.data.data
            : [];

        if (safe.length) {
          setProductList(safe);
          writeLookupCache('products', safe);
        }
        return safe;
      } catch (e) {
        unauthorize(e as ErrorLike);
        return [];
      }
    });
  }, [
    runOnce,
    token,
    readLookupCache,
    writeLookupCache,
    isCacheValid,
    unauthorize,
  ]);

  const loadSensors = useCallback(() => {
    if (!token) {
      return Promise.resolve([]);
    }
    return runOnce('sensors', async () => {
      const cached = readLookupCache('company_sensors');
      if (isCacheValid(cached)) {
        setSensorsList(cached);
        return cached;
      }

      try {
        // Company-connected manufacturers only (not the global Sensor catalog).
        const res = await GetSettingsCompanySensors(token);
        const payload = res?.data?.data as
          | {
              is_success?: boolean;
              data?: Array<{
                sensor_id?: string | number;
                sensor_name?: string;
                name?: string;
                manufacturer_name?: string | null;
              }>;
            }
          | undefined;
        const rows = Array.isArray(payload?.data) ? payload.data : [];

        const byId = new Map<string, ListItem>();
        for (const row of rows) {
          const id = String(row.sensor_id ?? '').trim();
          if (!id || byId.has(id)) continue;
          const label =
            (
              row.manufacturer_name ||
              row.sensor_name ||
              row.name ||
              ''
            ).trim() || id;
          byId.set(id, { id, name: label, title: label });
        }

        const safe = [...byId.values()].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
          })
        );

        if (safe.length) {
          setSensorsList(safe);
          writeLookupCache('company_sensors', safe);
        }
        return safe;
      } catch (e) {
        unauthorize(e as ErrorLike);
        return [];
      }
    });
  }, [
    runOnce,
    token,
    readLookupCache,
    writeLookupCache,
    isCacheValid,
    unauthorize,
  ]);

  const loadCouriers = useCallback(() => {
    if (!token) {
      return Promise.resolve([]);
    }
    return runOnce('couriers', async () => {
      const cached = readLookupCache('couriers');
      if (isCacheValid(cached)) {
        setCouriersList(cached);
        return cached;
      }

      if (isZoomlogiSupport(companyName)) {
        await dispatch(fetchAllCouriers(token));
        const safe = Array.isArray(storeCouriers) ? storeCouriers : [];
        if (safe.length) {
          setCouriersList(safe);
          writeLookupCache('couriers', safe);
        }
        return safe;
      }

      const val = await getServiceLevelCouriersDDL(token);
      const safe = Array.isArray(val) ? val : [];
      if (safe.length) {
        setCouriersList(safe);
        writeLookupCache('couriers', safe);
      }
      return safe;
    });
  }, [
    runOnce,
    readLookupCache,
    writeLookupCache,
    isCacheValid,
    companyName,
    dispatch,
    token,
    storeCouriers,
  ]);

  const fetchProductTypesPaginated = useCallback(
    async ({
      page,
      search,
    }: {
      page: number;
      limit: number;
      search?: string;
    }) => {
      const res = await GetProductTypesWithPaginationAndSearch(
        page,
        search ?? ''
      );
      const raw = res.data.data.data ?? [];
      const pageCount = res.data.data.page_count ?? 0;

      return { data: raw, pageCount };
    },
    []
  );

  type FetchLocationsArgs = {
    page: number;
    limit?: number;
    search?: string;
    filterType?: string;
  };

  type FetchLocationsResult = {
    data: GroupedLocationItem[];
    total: number;
    pageCount: number;
    hasNext: boolean;
  };

  type FetchParsedLocationsArgs = {
    page: number;
    limit?: number;
    search?: string;
  };

  interface ApiCity {
    id: string | number;
    name?: string;
    display?: string;
  }

  interface ApiState {
    id: string | number;
    name?: string;
    display_name?: string;
    cities?: ApiCity[];
  }

  interface NormalizedLocation extends ListItem {
    id: string;
    type: 'group' | 'single';
    name: string;
    locations: {
      id: string;
      name: string;
    }[];
  }

  type FetchParsedLocationsResult = {
    data: ListItem[];
    total: number;
    pageCount: number;
    hasNext: boolean;
  };

  const fetchParsedLocationsPaginated = useCallback(
    async ({
      search = '',
      page,
      limit,
    }: FetchParsedLocationsArgs): Promise<FetchParsedLocationsResult> => {
      if (!token) {
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }

      try {
        const res = await GetLocationsParsedListWithPagination(
          token,
          page,
          Math.min(Math.max(limit ?? 20, 1), 100),
          search
        );
        const root = res?.data?.data?.data;
        const items = Array.isArray(root?.items) ? root.items : [];

        const pagination = root?.pagination;

        const totalItems =
          typeof pagination?.total_items === 'number'
            ? pagination.total_items
            : items.length;

        const totalPages =
          typeof pagination?.total_pages === 'number'
            ? pagination.total_pages
            : 0;

        const normalizedItems: NormalizedLocation[] = items.map(
          (item: ApiState): NormalizedLocation => ({
            ...item,
            id: String(item.id),
            type:
              Array.isArray(item.cities) && item.cities.length > 0
                ? 'group'
                : 'single',
            name: item.display_name ?? item.name ?? '',
            locations: Array.isArray(item.cities)
              ? item.cities.map((city: ApiCity) => ({
                  id: String(city.id),
                  name: city.display ?? city.name ?? '',
                }))
              : [],
          })
        );

        return {
          data: normalizedItems,
          total: totalItems,
          pageCount: totalPages,
          hasNext: !!pagination?.has_next,
        };
      } catch (error) {
        unauthorize(error as ErrorLike);
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
    },
    [token, unauthorize]
  );

  const fetchLocationsPaginated = useCallback(
    async ({
      search = '',
      page,
      limit,
      filterType,
    }: FetchLocationsArgs): Promise<FetchLocationsResult> => {
      if (!token) {
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
      try {
        const res = await GetGroupedLocationsWithPagination(
          token,
          page,
          Math.min(Math.max(limit ?? 20, 1), 100),
          search,
          filterType
        );

        const root = res?.data?.data?.data;
        const items = Array.isArray(root?.items) ? root.items : [];

        const pagination = root?.pagination;

        const totalItems =
          typeof pagination?.total_items === 'number'
            ? pagination.total_items
            : items.length;

        const totalPages =
          typeof pagination?.total_pages === 'number'
            ? pagination.total_pages
            : 0;

        return {
          data: items,
          total: totalItems,
          pageCount: totalPages,
          hasNext: !!pagination?.has_next,
        };
      } catch (error) {
        unauthorize(error as ErrorLike);
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
    },
    [token, unauthorize]
  );

  const fetchInventoryLocationsPaginated = useCallback(
    async ({ search = '', page }: { page: number; search?: string }) => {
      if (!token) {
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }

      try {
        const res: GetLocationsWithPaginationAndSearchResponse =
          await GetLocationsWithPaginationAndSearch(token, false, page, search);

        const payload = res?.data?.data;
        const items = payload?.data?.data ?? [];
        const pageCount = payload?.page_count ?? 0;
        const totalCount = payload?.total_count ?? items.length;

        return {
          data: items.map((item: LocationData) => ({
            id: String(item?.id ?? ''),
            name: item?.name ?? '',
          })),
          total: totalCount,
          pageCount,
          hasNext: page < pageCount,
        };
      } catch (error) {
        unauthorize(error as ErrorLike);
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
    },
    [token, unauthorize]
  );
  const projectsDerived = useMemo(() => {
    if (projectsID?.productList?.length > 0) {
      return projectsID.productList
        .filter(
          (item) =>
            item?.project_id !== null &&
            item?.project_id !== undefined &&
            item?.project_id !== ''
        )
        .map((item) => ({ id: item.project_id, name: item.project_id }));
    }
    return [];
  }, [projectsID]);

  useMemo(() => {
    setProjectIdList(projectsDerived);
  }, [projectsDerived]);

  useMemo(() => {
    const pt = state?.ProductTypes?.productTypeList;
    if (Array.isArray(pt) && pt.length > 0) setProductTypeList(pt);
  }, [state]);

  const lookups = useMemo(
    () =>
      ({
        couriersList,
        productTypeList,
        productList,
        sensorsList,
        alertAssignmentOptions,
        locationTypeList,
        projectIdList,
        shipperRtgList,
        consigneeRtgList,
        allTeams,
        shipmentStatusDDL,
        shipmentStatusELDDL,
        healthStatusDDL,
        deliveryStatusDDL,
        PODStatusOptions,
        statesAndCities,
        fetchers: {
          product_type: fetchProductTypesPaginated,
          locations: fetchParsedLocationsPaginated,
          grouped_locations: fetchLocationsPaginated,
          inventory_locations: fetchInventoryLocationsPaginated,
        },
        loaders: {
          alertAssignment: loadAlertAssignment,
          locationTypes: loadLocationTypes,
          couriers: loadCouriers,
          teams: loadTeams,
          products: loadProducts,
          sensors: loadSensors,
          shipmentStatus: loadShipmentStatus,
          shipperRtg: loadShipperRtg,
          consigneeRtg: loadConsigneeRtg,
        },
        resetLookups,
      }) as FilterLookups & {
        alertAssignmentOptions?: ListItem[];
        loaders: {
          alertAssignment?: () => Promise<ListItem[]>;
          couriers: () => Promise<ListItem[]>;
          teams: () => Promise<ListItem[]>;
          products: () => Promise<ListItem[]>;
          sensors: () => Promise<ListItem[]>;
          shipmentStatus: (legType?: 'CL' | 'EL') => Promise<ListItem[]>;
          locationTypes: () => Promise<ListItem[]>;
          shipperRtg: () => Promise<ListItem[]>;
          consigneeRtg: () => Promise<ListItem[]>;
        };
        resetLookups: () => void;
      },
    [
      couriersList,
      productTypeList,
      productList,
      sensorsList,
      alertAssignmentOptions,
      loadAlertAssignment,
      locationTypeList,
      projectIdList,
      shipperRtgList,
      consigneeRtgList,
      allTeams,
      shipmentStatusDDL,
      shipmentStatusELDDL,
      statesAndCities,
      fetchProductTypesPaginated,
      fetchParsedLocationsPaginated,
      fetchLocationsPaginated,
      fetchInventoryLocationsPaginated,
      loadLocationTypes,
      loadCouriers,
      loadTeams,
      loadProducts,
      loadSensors,
      loadShipmentStatus,
      loadShipperRtg,
      loadConsigneeRtg,
      resetLookups,
    ]
  );

  return lookups;
}
